import InfiniteScrollProvider from '../../utils/InfiniteScrollProvider'
import { AccountMergeRequest } from './AccountMergeRequest'
import { AccountMergeRequestStore } from './AccountMergeRequestStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { toStream } from '../../utils/mobx-util'
import { AccountMergeRequestItemViewModel } from './AccountMergeRequestItemViewModel'
import { match } from '../../utils/pattern-match-util'
import { ViewModelList } from '../../infra/ViewModelList'

/**
 * Filter options for merge requests.
 */
export enum AccountMergeRequestFilterOption {
  /**
   * Indicates that CS action has yet to be taken against the merge request.
   */
  Pending,

  /**
   * Indicates that CS action has been taken against the merge request (it was either approved or rejected).
   */
  Processed,
}

/**
 * Used to indicates whether the merge request's status or action menu should be depicted.
 */
export enum StatusOrActions {
  /**
   * Indicates that the merge request's status should be displayed.
   */
  Status,

  /**
   * Indicates that the merge request's actions menu should be displayed.
   */
  Actions,
}

export class AccountMergeRequestSearchViewModel {
  /**
   * Merge Requests.
   */
  private readonly _mergeRequests = new ViewModelList({
    models: () => this.scrollProvider.items.slice(),
    create: (item) => this.makeViewModel(item),
  })

  private readonly _hubspotPortalId: string

  constructor(private readonly _store: AccountMergeRequestStore) {
    makeObservable(this)

    this._hubspotPortalId =
      process.env.NODE_ENV === 'production' ? '20336616' : '21144479'
  }

  /**
   * Whether to query for pending or processed account merge requests.
   */
  @observable
  filterOption = AccountMergeRequestFilterOption.Pending

  /**
   * Search text for merge requests, if any.
   */
  @observable
  searchText = ''

  /**
   * Scroll provider for merge requests.
   */
  scrollProvider = new InfiniteScrollProvider<
    AccountMergeRequest,
    AccountMergeRequestFilterOption
  >({
    limit: 200,
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) => this._store.list(paging, filter),
  })

  /**
   * Indicates whether the merge request's status or actions menu should be displayed.
   */
  @computed
  get statusOrActions() {
    return match(this.filterOption, {
      [AccountMergeRequestFilterOption.Pending]: () => StatusOrActions.Actions,
      [AccountMergeRequestFilterOption.Processed]: () => StatusOrActions.Status,
    })
  }

  /**
   * Individual merge requests filtered by the given search text (if any).
   */
  @computed
  get mergeRequests() {
    if (!this.searchText) {
      return this._mergeRequests.items
    }

    const text = this.searchText.toLowerCase()
    return this._mergeRequests.items.filter(
      (mr) =>
        mr.displayName.toLowerCase().includes(text) ||
        mr.email.toLowerCase().includes(text) ||
        mr.primaryAccountId.toLowerCase().includes(text) ||
        mr.duplicateAccountId.toLowerCase().includes(text),
    )
  }

  /**
   * Sets the filter option for searching.
   */
  @action.bound
  setFilterOption(filterOption: AccountMergeRequestFilterOption) {
    this.filterOption = filterOption
  }

  /**
   * Sets the search text for searching.
   */
  @action.bound
  setSearchText(text: string) {
    this.searchText = text
  }

  /**
   * Refreshes the scroll provider's items.
   */
  @action.bound
  async refreshItems() {
    await this.scrollProvider.fetch()
  }

  /**
   * Activates the View Model.
   */
  activate() {
    this._mergeRequests.activate()

    toStream(() => this.filterOption, true).subscribe(() => {
      this.scrollProvider.reset(false)
      this.scrollProvider.fetch(this.filterOption).then()
    })
  }

  /**
   * Helper factory for a view model representing a merge request item.
   */
  private makeViewModel(item: AccountMergeRequest) {
    return new AccountMergeRequestItemViewModel(
      item,
      this.statusOrActions,
      this._hubspotPortalId,
    )
  }
}
